// core/skillRegistry.js
// Central registry for skill modules

import { IS_DEV } from '../config/env.js';
import { getAtlasPercentBg } from '../data/atlasManifest.js';

/**
 * @typedef {Object} SkillTab
 * @property {string} id - Tab identifier
 * @property {string} name - Display name
 * @property {string} [icon] - Tab icon
 * @property {boolean} [default] - Is this the default tab
 */

/**
 * @typedef {Object} SkillIconSprite
 * @property {string} sheet - Path to sprite sheet
 * @property {number} x - X position in sprite sheet
 * @property {number} y - Y position in sprite sheet
 * @property {number} width - Sprite width
 * @property {number} height - Sprite height
 */

/**
 * @typedef {Object} SkillConfig
 * @property {string} id - Skill identifier (matches SKILLS enum)
 * @property {string} name - Display name
 * @property {string} icon - Skill icon emoji (fallback if no sprite)
 * @property {SkillIconSprite} [iconSprite] - Sprite-based icon (takes precedence over emoji)
 * @property {string} [description] - Skill description
 * @property {SkillTab[]} [tabs] - Sub-page tabs
 * @property {string} [defaultTab] - Default tab ID when navigating to skill
 * @property {string} [css] - Path to skill-specific CSS file
 * @property {Function} render - Render function(params) -> void
 * @property {Function} [update] - Update function called each tick
 * @property {Function} [init] - Initialization function called once
 * @property {Function} [cleanup] - Cleanup function called when leaving skill
 * @property {boolean} [hidden] - Hide from sidebar
 * @property {boolean} [devOnly] - Only show in dev mode
 */

class SkillRegistry {
    constructor() {
        /** @type {Map<string, SkillConfig>} */
        this.skills = new Map();
        this.initCallbacks = new Map();
        this.initialized = new Set();
    }

    /**
     * Register a skill module
     * @param {string} skillId - Skill identifier
     * @param {SkillConfig} config - Skill configuration
     */
    register(skillId, config) {
        if (this.skills.has(skillId)) {
            console.warn(`[SkillRegistry] Skill '${skillId}' already registered, overwriting`);
        }

        // Validate required fields
        if (!config.id) config.id = skillId;
        if (!config.name) {
            console.error(`[SkillRegistry] Skill '${skillId}' missing required 'name' field`);
            return;
        }
        if (!config.render) {
            console.error(`[SkillRegistry] Skill '${skillId}' missing required 'render' function`);
            return;
        }

        // Set defaults
        const normalizedConfig = {
            id: skillId,
            name: config.name,
            icon: config.icon || '📦',
            iconImage: config.iconImage || null,
            iconSprite: config.iconSprite || null,
            description: config.description || '',
            tabs: config.tabs || [],
            defaultTab: config.defaultTab || (config.tabs?.[0]?.id),
            css: config.css || null,
            render: config.render,
            update: config.update || null,
            init: config.init || null,
            cleanup: config.cleanup || null,
            hidden: config.hidden || false,
            devOnly: config.devOnly || false,
        };

        this.skills.set(skillId, normalizedConfig);
    }

    /**
     * Get a skill configuration by ID
     * @param {string} skillId
     * @returns {SkillConfig|undefined}
     */
    get(skillId) {
        return this.skills.get(skillId);
    }

    /**
     * Check if a skill is registered
     * @param {string} skillId
     * @returns {boolean}
     */
    has(skillId) {
        return this.skills.has(skillId);
    }

    /**
     * Get all registered skills
     * @returns {SkillConfig[]}
     */
    getAll() {
        return Array.from(this.skills.values());
    }

    /**
     * Get skills visible in sidebar
     * @param {boolean} [isDev] - Whether in dev mode (defaults to IS_DEV)
     * @returns {SkillConfig[]}
     */
    getVisible(isDev = IS_DEV) {
        return this.getAll().filter(skill => {
            if (skill.hidden) return false;
            if (skill.devOnly && !isDev) return false;
            return true;
        });
    }

    /**
     * Get skill IDs in registration order
     * @returns {string[]}
     */
    getSkillIds() {
        return Array.from(this.skills.keys());
    }

    /**
     * Initialize a skill (called once when first navigated to)
     * @param {string} skillId
     */
    async initSkill(skillId) {
        const skill = this.skills.get(skillId);
        if (!skill) return;

        if (this.initialized.has(skillId)) {
            return; // Already initialized
        }

        if (skill.init) {
            try {
                await skill.init();
            } catch (error) {
                console.error(`[SkillRegistry] Error initializing skill '${skillId}':`, error);
            }
        }

        this.initialized.add(skillId);
    }

    /**
     * Check if a skill has been initialized
     * @param {string} skillId
     * @returns {boolean}
     */
    isInitialized(skillId) {
        return this.initialized.has(skillId);
    }

    /**
     * Call cleanup on a skill (when navigating away)
     * @param {string} skillId
     */
    async cleanupSkill(skillId) {
        const skill = this.skills.get(skillId);
        if (!skill || !skill.cleanup) return;

        try {
            await skill.cleanup();
        } catch (error) {
            console.error(`[SkillRegistry] Error cleaning up skill '${skillId}':`, error);
        }
    }

    /**
     * Get the default tab for a skill
     * @param {string} skillId
     * @returns {string|null}
     */
    getDefaultTab(skillId) {
        const skill = this.skills.get(skillId);
        if (!skill) return null;

        // Check explicit default
        if (skill.defaultTab) return skill.defaultTab;

        // Check tabs array for default flag
        const defaultTab = skill.tabs?.find(t => t.default);
        if (defaultTab) return defaultTab.id;

        // Return first tab if any
        return skill.tabs?.[0]?.id || null;
    }

    /**
     * Check if a skill has tabs
     * @param {string} skillId
     * @returns {boolean}
     */
    hasTabs(skillId) {
        const skill = this.skills.get(skillId);
        return skill?.tabs?.length > 0;
    }

    /**
     * Get tabs for a skill
     * @param {string} skillId
     * @returns {SkillTab[]}
     */
    getTabs(skillId) {
        const skill = this.skills.get(skillId);
        return skill?.tabs || [];
    }

    /**
     * Check if a tab exists for a skill
     * @param {string} skillId
     * @param {string} tabId
     * @returns {boolean}
     */
    hasTab(skillId, tabId) {
        const skill = this.skills.get(skillId);
        return skill?.tabs?.some(t => t.id === tabId) || false;
    }

    /**
     * Unregister a skill (primarily for testing)
     * @param {string} skillId
     */
    unregister(skillId) {
        this.skills.delete(skillId);
        this.initialized.delete(skillId);
    }

    /**
     * Clear all registrations (primarily for testing)
     */
    clear() {
        this.skills.clear();
        this.initialized.clear();
    }

    /**
     * Get skill icon HTML (handles both emoji and sprite icons)
     * @param {string} skillId - Skill identifier
     * @returns {string} HTML string for the icon
     */
    getSkillIcon(skillId) {
        const skill = this.skills.get(skillId);
        if (!skill) return '📦';

        // Check for simple image icon (single image file, scaled to fit)
        if (skill.iconImage) {
            const atlas = getAtlasPercentBg(skill.iconImage);
            if (atlas) {
                return `<span class="skill-icon-img" style="background-image: url('${atlas.atlas}'); background-size: ${atlas.bgSize}; background-position: ${atlas.bgPosition};"></span>`;
            }
            return `<span class="skill-icon-img" style="background-image: url('${skill.iconImage}');"></span>`;
        }

        // Check for sprite-based icon (sprite sheet with x/y coordinates)
        if (skill.iconSprite) {
            const { sheet, x, y, width, height } = skill.iconSprite;

            // Very large sprites (>200px) at origin - treat as single image, use contain scaling
            if ((width > 200 || height > 200) && x <= 10 && y <= 10) {
                return `<span class="skill-sprite-large" style="background-image: url('${sheet}');"></span>`;
            }

            // Regular sprites - use traditional sprite sheet positioning with transform scaling
            const isLarge = width >= 48 || height >= 48;
            const sizeClass = isLarge ? 'sprite-icon-lg' : '';
            return `<span class="sprite-icon skill-sprite ${sizeClass}" style="background-image: url('${sheet}'); background-position: -${x}px -${y}px; width: ${width}px; height: ${height}px;"></span>`;
        }

        // Return emoji icon
        return skill.icon || '📦';
    }
}

// Export singleton instance
export const skillRegistry = new SkillRegistry();

/**
 * Standalone function to get skill icon HTML
 * @param {string} skillId - Skill identifier
 * @returns {string} HTML string for the icon
 */
export function getSkillIcon(skillId) {
    return skillRegistry.getSkillIcon(skillId);
}

export default skillRegistry;
